import dayjs from 'dayjs';

/** Fixed locale for all user-visible date/time strings. */
export const ENGLISH_LOCALE = 'en-US';

/**
 * Default IANA zone for deterministic tests (acceptance + unit).
 * Production call sites omit `timeZone` and use the device local zone via Intl.
 */
export const ENGLISH_DATETIME_TEST_ZONE = 'Europe/Berlin';

export type FormatEnglishDateTimeOptions = {
  /** IANA zone; omit to use the runtime local zone. */
  timeZone?: string;
};

function toDate(isoOrDate: string | Date): Date {
  return typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
}

function intlOptions(options?: FormatEnglishDateTimeOptions): Pick<Intl.DateTimeFormatOptions, 'timeZone'> {
  return options?.timeZone ? { timeZone: options.timeZone } : {};
}

/** 12-hour clock with AM/PM (e.g. "03:05 PM" locally, "05:05 PM" in pinned zones). */
export function formatEnglishTime(
  isoOrDate: string | Date,
  options?: FormatEnglishDateTimeOptions,
): string {
  if (options?.timeZone) {
    return new Intl.DateTimeFormat(ENGLISH_LOCALE, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: options.timeZone,
    }).format(toDate(isoOrDate));
  }
  return dayjs(isoOrDate).locale('en').format('hh:mm A');
}

/** Short weekday + day + month (e.g. "Wed, Oct 01"). */
export function formatEnglishDateShort(
  isoOrDate: string | Date,
  options?: FormatEnglishDateTimeOptions,
): string {
  return new Intl.DateTimeFormat(ENGLISH_LOCALE, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    ...intlOptions(options),
  }).format(toDate(isoOrDate));
}

/** Long weekday + month + day + year (e.g. "Wednesday, Oct 01, 2026"). */
export function formatEnglishDateLong(isoOrDate: string | Date): string {
  return dayjs(isoOrDate).locale('en').format('dddd, MMM DD, YYYY');
}

/** Full date for booking confirmations (e.g. "Wednesday, October 01, 2026"). */
export function formatEnglishDateWithYear(
  isoOrDate: string | Date,
  options?: FormatEnglishDateTimeOptions,
): string {
  return new Intl.DateTimeFormat(ENGLISH_LOCALE, {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...intlOptions(options),
  }).format(toDate(isoOrDate));
}

/** Compact month + day pill (e.g. "Oct 1"). */
export function formatEnglishMonthDay(isoOrDate: string | Date): string {
  return dayjs(isoOrDate).locale('en').format('MMM D');
}

/** Day-first month label for scheduled-change copy (e.g. "19 Oct"). */
export function formatEnglishDayMonth(
  isoOrDate: string | Date,
  options?: FormatEnglishDateTimeOptions,
): string {
  if (options?.timeZone) {
    const parts = new Intl.DateTimeFormat(ENGLISH_LOCALE, {
      day: 'numeric',
      month: 'short',
      timeZone: options.timeZone,
    }).formatToParts(toDate(isoOrDate));
    const day = parts.find((p) => p.type === 'day')?.value ?? '';
    const month = parts.find((p) => p.type === 'month')?.value ?? '';
    return `${day} ${month}`;
  }
  return dayjs(isoOrDate).locale('en').format('D MMM');
}

/** Calendar date without time (e.g. "10/01/2026"). */
export function formatEnglishCalendarDate(
  isoOrDate: string | Date,
  options?: FormatEnglishDateTimeOptions,
): string {
  return new Intl.DateTimeFormat(ENGLISH_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...intlOptions(options),
  }).format(toDate(isoOrDate));
}

/** Short timezone abbreviation for a given instant (e.g. "CEST"). */
export function formatEnglishTimezoneAbbr(
  isoOrDate: string | Date = new Date(),
  options?: FormatEnglishDateTimeOptions,
): string {
  const parts = new Intl.DateTimeFormat(ENGLISH_LOCALE, {
    timeZoneName: 'short',
    ...intlOptions(options),
  }).formatToParts(toDate(isoOrDate));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? 'Local';
}

/**
 * Oral-exam waiting lobby: time only when today, otherwise "Wed, 1 Oct · 3:05 PM".
 */
export function formatEnglishWaitingSlotStart(iso: string): string | null {
  const start = dayjs(iso);
  if (!start.isValid()) return null;
  const time = formatEnglishTime(start.toDate());
  if (start.isSame(dayjs(), 'day')) return time;
  const date = start.locale('en').format('ddd, D MMM');
  return `${date} · ${time}`;
}
